import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { LOCALE_LABELS, type SupportedLocale } from "@/i18n";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

const TENANT_LOCALES: SupportedLocale[] = ["de", "nl", "fr", "it", "es"];

export default function AdminLanguages() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allowed: isAdmin, isLoading } = useCanManageCompany();
  const { id: companyId } = useCurrentCompany();
  const [enabled, setEnabled] = useState<Set<SupportedLocale>>(new Set());
  const [pending, setPending] = useState<SupportedLocale | null>(null);

  useEffect(() => {
    if (!companyId) return;
    supabase
      .from("company_locales")
      .select("locale")
      .eq("company_id", companyId)
      .then(({ data }) => {
        setEnabled(new Set((data ?? []).map((r) => r.locale as SupportedLocale)));
      });
  }, [companyId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  // Access enforced by the route guard in App.tsx.

  const toggle = async (locale: SupportedLocale, on: boolean) => {
    if (!companyId) return;
    setPending(locale);
    try {
      if (on) {
        const { error } = await supabase
          .from("company_locales")
          .insert({ company_id: companyId, locale });
        if (error) throw error;
        setEnabled((prev) => new Set(prev).add(locale));
      } else {
        const { error } = await supabase
          .from("company_locales")
          .delete()
          .eq("company_id", companyId)
          .eq("locale", locale);
        if (error) throw error;
        setEnabled((prev) => {
          const next = new Set(prev);
          next.delete(locale);
          return next;
        });
      }
      toast({ title: t("app.saved") + " ✓" });
    } catch (e: any) {
      toast({ title: t("app.error"), description: e.message, variant: "destructive" });
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-4">
        <SettingsBackLink to="/settings/company" label={t("nav.companySettings", { ns: "settings" })} />

        <div>
          <div className="flex items-center gap-2">
            <Languages className="w-5 h-5 text-primary" />
            <h1 className="text-2xl font-bold">{t("language.companyLanguages")}</h1>
          </div>
          <p className="text-muted-foreground text-sm max-w-2xl">
            {t("language.companyLanguagesHelp")}
          </p>
        </div>
      </div>


      <div className="px-6 max-w-2xl space-y-3">
        <Card className="p-4 flex items-center justify-between opacity-70">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{LOCALE_LABELS.en.flag}</span>
            <div>
              <div className="font-medium">{LOCALE_LABELS.en.native}</div>
              <div className="text-xs text-muted-foreground">{t("language.enabled")} ({t("language.alwaysOn")})</div>
            </div>
          </div>
          <Switch checked disabled />
        </Card>

        {TENANT_LOCALES.map((locale) => {
          const isOn = enabled.has(locale);
          return (
            <Card key={locale} className="p-4 flex items-center justify-between">
              <Label htmlFor={`lng-${locale}`} className="flex items-center gap-3 cursor-pointer">
                <span className="text-2xl">{LOCALE_LABELS[locale].flag}</span>
                <span className="font-medium">{LOCALE_LABELS[locale].native}</span>
              </Label>
              <Switch
                id={`lng-${locale}`}
                checked={isOn}
                disabled={pending === locale}
                onCheckedChange={(v) => toggle(locale, v)}
              />
            </Card>
          );
        })}
      </div>
    </div>
  );
}
